// Project — data access (docs/design/project.md, PR 1).
//
//   list     rpc project_page(company, status, term, filters, sort, after, limit 51): keyset on
//            code within projects_list_idx (a company has hundreds of projects at most).
//   detail   rpc project_document(id): header, WBS with booked hours and percent,
//            the latest 50 time entries — one round-trip.
//   writes   save_project (header + WBS as a set, expected_revision),
//            set_project_status, book_project_time.
//   my time  rpc my_time_entries(from, to): the caller's entries (project_time_entries_user_idx).
//   pegging  rpc peg_candidates(project, target, term) (≤ 20 rows) and set_order_peg (PR 2);
//            the document carries its pegs (partial *_project_idx indexes).
// Hours travel as decimal strings (Postgres numeric), never floats.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";
import { projectStatuses, sortParam, type ListFilter, type ListSort } from "@patronix/domain/list";

export { projectStatuses };
export type ProjectStatus = (typeof projectStatuses)[number];
export const projectFilters = ["open", ...projectStatuses, "all"] as const;
export type ProjectFilter = (typeof projectFilters)[number];
export const isProjectFilter = (v: unknown): v is ProjectFilter => (projectFilters as readonly unknown[]).includes(v);
export type ProjectAction = "activate" | "complete" | "reopen" | "close" | "cancel";

export const projectTone = { draft: "neutral", active: "info", completed: "success", closed: "neutral", cancelled: "neutral" } as const;

/** Actions offered per status (the database decides). */
export const projectActions: Record<ProjectStatus, ProjectAction[]> = {
  draft: ["activate", "cancel"],
  active: ["complete", "cancel"],
  completed: ["reopen", "close"],
  closed: [],
  cancelled: [],
};

export const PROJECT_PAGE = 50;
const STALE = 60_000;

export interface ProjectRow {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  customer_code: string | null;
  customer_name: string | null;
  manager_name: string | null;
  start_date: string | null;
  end_date: string | null;
  activity_count: number;
  planned_hours: string | number;
  booked_hours: string | number;
}

export interface ProjectActivity {
  id: string;
  code: string;
  name: string;
  kind: "phase" | "activity";
  parent_code: string | null;
  seq: number;
  planned_start: string | null;
  planned_end: string | null;
  planned_hours: string | number;
  booked_hours: string | number;
  is_milestone: boolean;
  is_done: boolean;
  percent: number;
}

export interface ProjectTimeEntry {
  id: string;
  work_date: string;
  hours: string | number;
  note: string | null;
  created_at: string;
  activity_code: string;
  user_name: string | null;
}

export const pegTargets = ["purchase_order_line", "sales_order_line", "production_order"] as const;
export type PegTarget = (typeof pegTargets)[number];
/** The document permission that may peg each target. */
export const pegPermission: Record<PegTarget, string> = {
  purchase_order_line: "procurement.po.manage",
  sales_order_line: "sales.order.manage",
  production_order: "production.order.manage",
};

export interface ProjectPeg {
  target: PegTarget;
  id: string;
  document_id: string;
  number: string | null;
  line_no: number | null;
  partner_code: string | null;
  item_code: string;
  item_description: string;
  uom_code: string;
  uom_decimals: number;
  qty: string | number;
  done_qty: string | number;
  due_date: string | null;
  document_status: string;
  line_status: string;
  is_open: boolean;
  activity_code: string | null;
  can_unpeg: boolean;
}

export interface PegCandidate {
  id: string;
  document_id: string;
  number: string | null;
  line_no: number | null;
  partner_code: string | null;
  item_code: string;
  item_description: string;
  qty: string | number;
  uom_code: string;
  due_date: string | null;
  status: string;
}

export interface ProjectDocument {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  company_id: string;
  customer_id: string | null;
  customer_code: string | null;
  customer_name: string | null;
  manager_user_id: string | null;
  manager_name: string | null;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
  planned_hours: string | number;
  booked_hours: string | number;
  activity_count: number;
  revision: number;
  created_at?: string;
  activated_at?: string | null;
  completed_at?: string | null;
  closed_at?: string | null;
  activities: ProjectActivity[];
  time: ProjectTimeEntry[];
  /** Pegged documents (PR 2); absent from older payloads. */
  pegs?: ProjectPeg[];
}

export interface ActivityInput {
  code: string;
  name: string;
  kind: "phase" | "activity";
  parent_code: string | null;
  planned_start: string | null;
  planned_end: string | null;
  planned_hours: string | null;
  is_milestone: boolean;
  is_done: boolean;
}

export interface ProjectHeaderInput {
  code?: string | null;
  name: string;
  customer_id: string | null;
  manager_user_id: string | null;
  start_date: string | null;
  end_date: string | null;
  note: string | null;
  expected_revision?: number;
}

export interface MyTimeEntry {
  id: string;
  project_id: string;
  project_code: string;
  project_name: string;
  activity_id: string;
  activity_code: string;
  activity_name: string;
  work_date: string;
  hours: string | number;
  note: string | null;
  created_at: string;
}

export interface ProjectListParams {
  companyId: string;
  status: ProjectFilter;
  q: string;
  /** Column filters and sort (project_page spec, projectListColumns). */
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export const projectKeys = {
  all: ["project"] as const,
  list: (p: ProjectListParams) => ["project", "list", p] as const,
  detail: (id: string) => ["project", "detail", id] as const,
  myTime: (from: string, to: string) => ["project", "my-time", from, to] as const,
  candidates: (projectId: string, target: PegTarget, term: string) => ["project", "peg-candidates", projectId, target, term] as const,
  stock: (projectId: string) => ["project", "stock", projectId] as const,
};

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}
const demoOff = () => new Error("Projects are not available in demo mode");

export async function listProjects(p: ProjectListParams, cursor: string | null, signal: AbortSignal, size = PROJECT_PAGE): Promise<Page<ProjectRow>> {
  if (isDemoMode || !p.companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("project_page", {
      p_company_id: p.companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (ProjectRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getProject(id: string, signal: AbortSignal): Promise<ProjectDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("project_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as ProjectDocument | null) ?? null;
}

export function projectListQuery(p: ProjectListParams) {
  return infiniteQueryOptions({
    queryKey: projectKeys.list(p),
    queryFn: ({ pageParam, signal }) => listProjects(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<ProjectRow>) => last.nextCursor,
    staleTime: STALE,
    enabled: !!p.companyId,
  });
}

export function projectDetailQuery(id: string) {
  return queryOptions({ queryKey: projectKeys.detail(id), queryFn: ({ signal }) => getProject(id, signal), staleTime: STALE });
}

async function getMyTime(from: string, to: string, signal: AbortSignal): Promise<MyTimeEntry[]> {
  if (isDemoMode) return [];
  const { data, error } = await db().rpc("my_time_entries", { p_from: from, p_to: to, p_limit: 500 }).abortSignal(signal);
  if (error) throw error;
  return data as MyTimeEntry[];
}

export function myTimeQuery(from: string, to: string) {
  return queryOptions({ queryKey: projectKeys.myTime(from, to), queryFn: ({ signal }) => getMyTime(from, to, signal), staleTime: 30_000 });
}

export async function saveProject(id: string | null, companyId: string, header: ProjectHeaderInput, activities: ActivityInput[]) {
  if (isDemoMode) throw demoOff();
  const { data, error } = await db().rpc("save_project", { p_id: id, p_company_id: companyId, p_header: header, p_activities: activities });
  if (error) throw error;
  return data as { id: string; code: string; revision: number };
}

export async function setProjectStatus(id: string, revision: number, action: ProjectAction) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("set_project_status", { p_id: id, p_revision: revision, p_action: action });
  if (error) throw error;
}

export async function bookTime(projectId: string, activityId: string, workDate: string, hours: string, note: string | null) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("book_project_time", {
    p_project_id: projectId,
    p_activity_id: activityId,
    p_work_date: workDate,
    p_hours: hours,
    p_note: note,
  });
  if (error) throw error;
}

export function pegCandidatesQuery(projectId: string, target: PegTarget, term: string) {
  return queryOptions({
    queryKey: projectKeys.candidates(projectId, target, term),
    queryFn: async ({ signal }): Promise<PegCandidate[]> => {
      if (isDemoMode) return [];
      const { data, error } = await db()
        .rpc("peg_candidates", { p_project_id: projectId, p_target: target, p_term: term.trim() || null, p_limit: 20 })
        .abortSignal(signal);
      if (error) throw error;
      return data as PegCandidate[];
    },
    staleTime: 15_000,
  });
}

export interface ProjectProgressRow {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  customer_name: string | null;
  start_date: string | null;
  end_date: string | null;
  planned_hours: string | number;
  booked_hours: string | number;
  earned_hours: string | number;
  milestones: number;
  milestones_done: number;
  purchase_lines: number;
  purchase_open: number;
  purchase_done: string | number;
  purchase_qty: string | number;
  sales_lines: number;
  sales_open: number;
  sales_done: string | number;
  sales_qty: string | number;
  production_orders: number;
  production_open: number;
  production_done: string | number;
  production_qty: string | number;
  stock_lines: number;
}

/** Progress of the company's projects (project_progress_page, ≤ 500 rows, one page). */
export async function listProgress(companyId: string, status: ProjectFilter, signal: AbortSignal): Promise<Page<ProjectProgressRow>> {
  if (isDemoMode || !companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db().rpc("project_progress_page", { p_company_id: companyId, p_status: status }).abortSignal(signal);
  if (error) throw error;
  return { rows: data as ProjectProgressRow[], nextCursor: null };
}

export function progressQuery(companyId: string, status: ProjectFilter) {
  return infiniteQueryOptions({
    queryKey: ["project", "progress", companyId, status] as const,
    queryFn: ({ signal }) => listProgress(companyId, status, signal),
    initialPageParam: null as string | null,
    getNextPageParam: () => null,
    staleTime: STALE,
    enabled: !!companyId,
  });
}

/** Share of done in total, 0–100; null when nothing is planned / ordered. */
export function share(done: string | number, total: string | number): number | null {
  const t = Number(total) || 0;
  return t > 0 ? Math.round((100 * Math.min(Number(done) || 0, t)) / t) : null;
}

export interface ProjectStockRow {
  item_id: string;
  item_code: string;
  item_description: string;
  uom_code: string;
  uom_decimals: number;
  warehouse_id: string;
  warehouse_code: string;
  location_id: string;
  location_code: string;
  lot_id: string | null;
  lot_code: string | null;
  on_hand: string | number;
}

/** Stock of the project (project_stock, inventory_balances_project_idx, ≤ 200 rows). */
export function projectStockQuery(projectId: string) {
  return queryOptions({
    queryKey: projectKeys.stock(projectId),
    queryFn: async ({ signal }): Promise<ProjectStockRow[]> => {
      if (isDemoMode) return [];
      const { data, error } = await db().rpc("project_stock", { p_id: projectId }).abortSignal(signal);
      if (error) throw error;
      return data as ProjectStockRow[];
    },
    staleTime: 30_000,
  });
}

export async function setOrderPeg(target: PegTarget, id: string, projectId: string | null, activityId: string | null) {
  if (isDemoMode) throw demoOff();
  const { error } = await db().rpc("set_order_peg", { p_target: target, p_id: id, p_project_id: projectId, p_activity_id: activityId });
  if (error) throw error;
}

/** "PO-2026-00012 / 2" — the document number and, for lines, the line number. */
export function pegLabel(p: Pick<ProjectPeg, "number" | "line_no">): string {
  return `${p.number ?? "—"}${p.line_no ? ` / ${p.line_no}` : ""}`;
}

/**
 * Earned-hours progress of a project, 0–100: a done activity earns its plan,
 * an open one its booked hours up to the plan. null when nothing is planned.
 */
export function projectPercent(activities: Pick<ProjectActivity, "planned_hours" | "booked_hours" | "is_done">[]): number | null {
  let planned = 0;
  let earned = 0;
  for (const a of activities) {
    const p = Number(a.planned_hours) || 0;
    planned += p;
    earned += a.is_done ? p : Math.min(Math.max(Number(a.booked_hours) || 0, 0), p);
  }
  return planned > 0 ? Math.round((100 * earned) / planned) : null;
}

/** Monday (ISO week start) of the week containing the given local date, as YYYY-MM-DD. */
export function weekStart(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  const shift = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - shift);
  return localIso(d);
}

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return localIso(d);
}

function localIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Hours input: up to two decimals, comma or point; null when not a number. */
export function parseHours(v: string): string | null {
  const s = v.trim().replace(",", ".");
  return /^-?\d{1,4}(\.\d{1,2})?$/.test(s) ? s : null;
}

export interface MemberOption {
  user_id: string;
  email: string;
  full_name: string | null;
  status: "active" | "disabled";
}

/** Organization members for the manager choice (needs core.user.read). */
export const memberOptionsQuery = queryOptions({
  queryKey: ["project", "members"] as const,
  queryFn: async ({ signal }): Promise<MemberOption[]> => {
    if (isDemoMode) return [];
    const { data, error } = await db().rpc("tenant_members").abortSignal(signal);
    if (error) throw error;
    return (data as MemberOption[]).filter((m) => m.status === "active");
  },
  staleTime: 5 * 60_000,
});

/** Depth of each activity in the tree (0 = root), for indentation. */
export function activityDepths(activities: Pick<ProjectActivity, "code" | "parent_code">[]): Map<string, number> {
  const parent = new Map(activities.map((a) => [a.code, a.parent_code]));
  const depth = new Map<string, number>();
  const of = (code: string, guard = 0): number => {
    const known = depth.get(code);
    if (known !== undefined) return known;
    const p = parent.get(code);
    const d = p && guard < 50 ? of(p, guard + 1) + 1 : 0;
    depth.set(code, d);
    return d;
  };
  for (const a of activities) of(a.code);
  return depth;
}

const lineRules: [RegExp, string][] = [
  [/a code is required|code: letters/, "project.err.code"],
  [/appears twice/, "project.err.codeTwice"],
  [/a name of 1 to 160/, "project.err.name"],
  [/is not a phase/, "project.err.parentPhase"],
  [/unknown parent/, "project.err.parent"],
  [/own parent/, "project.err.parent"],
  [/planned end is before/, "project.err.dates"],
  [/planned hours/, "project.err.hours"],
];
const rules: [RegExp, string][] = [
  [/changed by someone else/, "project.err.stale"],
  [/already exists in this company/, "project.err.duplicate"],
  [/cycle/, "project.err.cycle"],
  [/has booked time and cannot be removed/, "project.err.booked"],
  [/Add at least one activity/, "project.err.noActivities"],
  [/Time is booked on the project/, "project.err.cancelBooked"],
  [/Time can be booked only on active projects/, "project.err.notActive"],
  [/is done; reopen it/, "project.err.done"],
  [/below zero hours/, "project.err.belowZero"],
  [/Hours: between/, "project.err.bookHours"],
  [/work date must be within/, "project.err.workDate"],
  [/manager must be an active member/, "project.err.manager"],
  [/end date is before/, "project.err.dates"],
  [/cannot be (activated|completed|reopened|closed|cancelled)/, "project.err.transition"],
  [/Orders can be pegged only to active projects/, "project.err.pegNotActive"],
  [/Stock has already moved/, "project.err.pegMoved"],
  [/Only open lines of open orders|Only planned or released production orders/, "project.err.pegClosed"],
  [/activity is not part of the project/, "project.err.pegActivity"],
  [/Orders are pegged to the project/, "project.err.cancelPegged"],
  [/Pegged orders are still open/, "project.err.closePegged"],
  [/Stock of the project remains/, "project.err.stockRemains"],
  [/permission|row-level security|42501/i, "project.err.permission"],
];

export function projectErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^Activity (\d+): (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("project.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1]) : (line[2] ?? "") });
  }
  const booked = /Activity (\S+) has booked time/.exec(message);
  if (booked) return tr("project.err.booked", { code: booked[1] ?? "" });
  const pegged = /Activity (\S+) is pegged to orders/.exec(message);
  if (pegged) return tr("project.err.activityPegged", { code: pegged[1] ?? "" });
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
